import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { EASE } from "@/components/Motion";
import TopBar from "@/components/TopBar";

const LINKS = [
  { to: "/collections", label: "Collections" },
  { to: "/kitchens-wardrobes", label: "Kitchens & Wardrobes" },
  { to: "/about", label: "About" },
  { to: "/blog", label: "Blog" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <>
      <header
        data-testid="site-header"
        className="fixed top-0 inset-x-0 z-50 bg-background/85 backdrop-blur-xl border-b border-border"
      >
        <TopBar />
        <div className="max-w-7xl mx-auto px-6 md:px-12 h-16 md:h-24 flex items-center justify-between">
          <Link to="/" data-testid="nav-logo">
            <img src="/assets/vm-logo-hd.png" alt="Varun Marbles" className="h-11 md:h-[78px] w-auto" />
          </Link>

          <nav className="hidden lg:flex items-center gap-8" data-testid="nav-desktop">
            {LINKS.map((l) => (
              <NavLink
                key={l.to}
                to={l.to}
                data-testid={`nav-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                className={({ isActive }) =>
                  `text-xs tracking-[0.2em] uppercase transition-colors duration-300 ${
                    isActive ? "text-foreground" : "text-muted-foreground hover:text-foreground"
                  }`
                }
              >
                {l.label}
              </NavLink>
            ))}
            <Link
              to="/contact"
              data-testid="nav-enquire-btn"
              className="group inline-flex items-center gap-2 bg-primary text-primary-foreground text-xs tracking-[0.2em] uppercase px-6 py-3 transition-colors duration-300 hover:bg-foreground/80"
            >
              Enquire
              <ArrowUpRight className="w-3.5 h-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
            </Link>
          </nav>

          <button
            data-testid="nav-menu-toggle"
            onClick={() => setOpen(!open)}
            className="lg:hidden text-foreground p-2"
            aria-label="Toggle menu"
          >
            {open ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="nav-mobile-menu"
            className="fixed inset-0 z-40 bg-background flex flex-col justify-center px-8 lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.35, ease: "easeOut" }}
          >
            <nav className="space-y-2">
              {[{ to: "/", label: "Home" }, ...LINKS].map((l, i) => (
                <div key={l.to} className="overflow-hidden">
                  <motion.div
                    initial={{ y: "110%" }}
                    animate={{ y: 0 }}
                    transition={{ duration: 0.7, ease: EASE, delay: 0.08 + i * 0.07 }}
                  >
                    <NavLink
                      to={l.to}
                      data-testid={`nav-mobile-link-${l.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                      className="block font-serif text-4xl font-light tracking-tight py-2 text-foreground"
                    >
                      {l.label}
                    </NavLink>
                  </motion.div>
                </div>
              ))}
            </nav>
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.5 }}
              className="mt-16 text-xs tracking-[0.2em] uppercase text-muted-foreground"
            >
              Purveyors of fine stone
            </motion.p>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
